import { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { FaAngleLeft, FaAngleRight, FaMapMarkedAlt, FaRupeeSign, FaUserTie } from 'react-icons/fa';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './productpage.css';
import Sidebar from '../Components/Sidebar';
import Topbar from '../Components/Topbar';
import useMidViewProductDetails from "../../middleware/Products/MidViewProductDetails";
import noPreviewImg from '../../assets/images/noPreview.png';

const ProductPage = () => {
  const { type } = useParams();
  const [issidebarOpen, setIssidebarOpen] = useState(true);
  const thumbnailContainerRef = useRef(null);
  const [zoomStyle, setZoomStyle] = useState({});
  const [currentIndex, setCurrentIndex] = useState(0);

  // Get data from middleware hook
  const {
    users,
    productDetails,
    isLoading,
    selectedVariant,
    mainImage,
    handleVariantSelect,
    setMainImage
  } = useMidViewProductDetails();

  // Extract user data
  const userData = Array.isArray(users) && users.length > 0 ? {
    AppID: users[0]['AppID'],
    ProfilePic: users[0]['ProfilePic'],
    usertype: users[0]['UserType'],
    username: users[0]['FullName'],
    CompanyName: users[0]['CompanyName'],
    EmailAddress: users[0]['EmailAddress']
  } : {};

  // Image navigation functions
  const nextImage = () => {
    if (productDetails?.images?.length > 0) {
      const newIndex = (currentIndex + 1) % productDetails.images.length;
      setMainImage(productDetails.images[newIndex].url);
      setCurrentIndex(newIndex);
    }
  };

  const prevImage = () => {
    if (productDetails?.images?.length > 0) {
      const newIndex = (currentIndex - 1 + productDetails.images.length) % productDetails.images.length;
      setMainImage(productDetails.images[newIndex].url);
      setCurrentIndex(newIndex);
    }
  };

  // Thumbnail scrolling
  const scrollThumbnails = (direction) => {
    const container = thumbnailContainerRef.current;
    const scrollAmount = 200;
    if (container) {
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  // Zoom effects
  const handleMouseHoverMove = (e) => {
    const { left, top, width, height } = e.target.getBoundingClientRect();
    const x = ((e.clientX - left) / width) * 100;
    const y = ((e.clientY - top) / height) * 100;

    setZoomStyle({
      transformOrigin: `${x}% ${y}%`,
      transform: "scale(2)"
    });
  };

  const handleMouseLeave = () => setZoomStyle({});

  // Toggle sidebar
  const toggleSidebar = () => setIssidebarOpen(!issidebarOpen);
  const stateData = productDetails?.prizeList?.filter(item => item.check_price_flag === 1) || [];
  const distributorData = productDetails?.prizeList?.filter(item => item.check_price_flag === 2) || [];

  const [activeTab, setActiveTab] = useState("state");

  return (
    <div className='md:flex md:flex-row md:w-full'>
      {/* Sidebar */}
      <div className={`${issidebarOpen ? 'w-0 md:w-1/5' : 'w-0'}`}>
        <Sidebar
          handleSideBar={toggleSidebar}
          issidebarOpen={issidebarOpen}
          UserTypeData={userData.usertype}
          userName={userData.username}
          appID={userData.AppID}
          profilePic={userData.ProfilePic}
          currentUserType={type}
        />
      </div>

      {/* Main Content */}
      <div className={`${issidebarOpen ? 'w-full md:ml-2 md:w-[80%]' : 'ml-20 w-full'}`}>
        <Topbar
          issidebarOpen={issidebarOpen}
          UserTypeData={userData.usertype}
          currentUserType={type}
        />
        <ToastContainer />

        {isLoading ? (
          <div className="flex justify-center items-center h-screen">
            <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <div className="w-full">
            <div className="col-start-1 col-end-13">
              <div className="p-2 text-xl text-[#1e3a8a]">
                <div className='px-3'>
                  <h1 className='text-lg text-theme-color-text font-bold'>Products</h1>
                  <p className='text-gray-600 text-[16px]'>
                    Select attributes as per your products range, code, name and category are required attributes.
                  </p>
                </div>
              </div>

              {/* Product Details Card */}
              <div className="border-0 flex-row rounded-xl m-3 px-3 bg-white shadow-xs">
                <div className='p-3'>
                  <div className="max-w-7xl mx-auto p-4 sm:p-6 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">

                    {/* Left Side - Image Gallery */}
                    <div className="relative space-y-3 md:space-y-4">
                      {/* Main Image */}
                      <div className="relative">
                        <div
                          className="overflow-hidden rounded-lg"
                          onMouseMove={handleMouseHoverMove}
                          onMouseLeave={handleMouseLeave}
                        >
                          <img
                            loading="lazy"
                            src={mainImage || noPreviewImg}
                            alt={productDetails?.product?.product_name || "Product"}
                            className="w-full h-[400px] sm:h-[500px] md:h-[600px] object-contain transition duration-300"
                            style={zoomStyle}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = noPreviewImg;
                            }}
                          />
                        </div>

                        {/* Navigation Buttons */}
                        {productDetails?.images?.length > 1 && (
                          <>
                            <button
                              onClick={prevImage}
                              className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-[#002080] opacity-35 hover:bg-[#00208075] text-white p-1 md:p-2 rounded-full"
                            >
                              <FaAngleLeft size={20} />
                            </button>
                            <button
                              onClick={nextImage}
                              className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-[#002080] opacity-35 hover:bg-[#00208075] text-white p-1 md:p-2 rounded-full"
                            >
                              <FaAngleRight size={20} />
                            </button>
                          </>
                        )}
                      </div>

                      {/* Thumbnails */}
                      {productDetails?.images?.length > 1 && (
                        <div className="relative">
                          <button
                            onClick={() => scrollThumbnails('left')}
                            className="absolute left-0 top-1/2 transform -translate-y-1/2 bg-[#002080] opacity-35 hover:bg-[#00208075] text-white p-1 rounded-full z-10"
                          >
                            <FaAngleLeft size={16} />
                          </button>

                          <div
                            className="thumbnails-container flex gap-2 overflow-x-auto pb-2 scroll-smooth"
                            ref={thumbnailContainerRef}
                          >
                            {productDetails.images.map((image, index) => (
                              <img
                                key={index}
                                src={image.url}
                                alt={`Thumbnail ${index + 1}`}
                                className={`w-20 h-20 md:w-24 md:h-24 object-cover rounded-lg cursor-pointer border-2 transition duration-300 ${mainImage === image.url ? 'border-blue-500' : 'border-gray-300'
                                  }`}
                                onClick={() => {
                                  setMainImage(image.url);
                                  setCurrentIndex(index);
                                }}
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = noPreviewImg;
                                }}
                              />
                            ))}
                          </div>

                          <button
                            onClick={() => scrollThumbnails('right')}
                            className="absolute right-0 top-1/2 transform -translate-y-1/2 bg-[#002080] opacity-35 hover:bg-[#00208075] text-white p-1 rounded-full z-10"
                          >
                            <FaAngleRight size={16} />
                          </button>
                        </div>
                      )}


                    </div>


                    {/* Right Side - Product Details */}
                    <div className="mt-4 md:mt-0">
                      {/* Product Badge */}
                      <span className="bg-yellow-100 text-yellow-800 text-xs font-semibold px-2 py-1 rounded">
                        {productDetails?.product?.badge_name || "New Product"} 
                      </span>

                      {/* Product Name */}
                      <h1 className="text-2xl sm:text-3xl font-bold mt-2">
                        {productDetails?.product?.product_name || "Product Name"}
                      </h1>

                      {/* Short Description */}
                      {productDetails?.product?.product_short_description && (
                        <p className="text-gray-600 mt-2 text-lg">
                          {productDetails.product.product_short_description}
                        </p>
                      )}

                      {/* Price */}
                      <div className='pt-3'>
                        <p className='text-2xl font-semibold text-gray-700'>
                          ₹{selectedVariant?.productPrice || productDetails?.variants?.[0]?.productPrice || "N/A"}
                        </p>
                        <p className='font-semibold text-lg'>Inclusive of all taxes</p>

                        {/* Stock Status */}
                        {selectedVariant?.productStock !== undefined && (
                          <p className={`text-sm mt-1 ${parseInt(selectedVariant.productStock) > 0
                            ? 'text-green-600'
                            : 'text-red-600'
                            }`}>
                            {/* {parseInt(selectedVariant.productStock) > 0 
                              ? `${selectedVariant.productStock} in stock` 
                              : 'Out of stock'} */}
                          </p>
                        )}
                      </div>

                      {/* Variant Selection */}
                      {productDetails?.variants?.length > 0 && (
                        <div className="mt-6 space-y-4">
                          {/* Color Variants */}
                          {/* {[...new Set(productDetails.variants.map(v => v.productVariant))].length > 0 && (
                            <div>
                              <h3 className="text-lg font-semibold mb-2">Colors</h3>
                              <div className="flex flex-wrap gap-2">
                                {[...new Set(productDetails.variants.map(v => v.productVariant))].map((color) => {
                                  const variant = productDetails.variants.find(v => v.productVariant === color);
                                  return (
                                    <button
                                      key={color}
                                      className={`w-10 h-10 rounded-full border-2 flex items-center justify-center ${
                                        selectedVariant?.productVariant === color
                                          ? 'border-blue-500 ring-2 ring-blue-200'
                                          : 'border-gray-300'
                                      }`}
                                      style={{ backgroundColor: color.toLowerCase() }}
                                      onClick={() => handleVariantSelect(variant)}
                                      title={color}
                                    />
                                  );
                                })}
                              </div>
                            </div>
                          )} */}

                          {/* Size/Capacity Variants */}
                          {[...new Set(productDetails.variants.map(v => v.measurement))].length > 0 && (
                            <div>
                              <h3 className="text-lg font-semibold mb-2">Variant</h3>
                              <div className="flex flex-wrap gap-2">
                                {[...new Set(productDetails.variants.map(v => v.productVariant))].map((size) => {
                                  const variant = productDetails.variants.find(v => v.productVariant === size);
                                  return (
                                    <button
                                      key={size}
                                      className={`px-4 py-2 border rounded-lg ${selectedVariant?.measurement === size
                                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                                        : 'border-gray-300 hover:bg-gray-50'
                                        }`}
                                      onClick={() => handleVariantSelect(variant)}
                                    >
                                      {size}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Long Description */}
                      <p className="mt-4 sm:mt-6 text-justify text-gray-600 text-[16px]">
                        {productDetails?.product?.product_long_description ||
                          "No detailed description available for this product."}
                      </p>

                      {/* Add to Cart Button */}
                      {/* <button
                        className="mt-4 sm:mt-6 w-full sm:w-auto bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
                        disabled={!selectedVariant || parseInt(selectedVariant?.productStock || '0') <= 0}
                      >
                        {/* {parseInt(selectedVariant?.productStock || '0') <= 0 
                          ? 'Out of Stock' 
                          : 'Add to Cart'} 

                      </button> */}
                    </div>
                  </div>

                        
                  {productDetails?.prizeList?.length > 0 && (
                    <div className="p-4">
                      {/* Tabs */}
                      <div className="flex space-x-4 mb-4">
                        <button
                          onClick={() => setActiveTab("state")}
                          className={`px-4 py-2 rounded-full text-sm font-semibold transition ${activeTab === "state"
                              ? "bg-blue-600 text-white"
                              : "bg-gray-200 text-gray-700 hover:bg-blue-100"
                            }`}
                        >
                          <FaMapMarkedAlt className="inline mr-2" />
                          State Prices
                        </button>
                        <button
                          onClick={() => setActiveTab("distributor")}
                          className={`px-4 py-2 rounded-full text-sm font-semibold transition ${activeTab === "distributor"
                              ? "bg-purple-600 text-white"
                              : "bg-gray-200 text-gray-700 hover:bg-purple-100"
                            }`}
                        >
                          <FaUserTie className="inline mr-2" />
                          Distributor Prices
                        </button>
                      </div>

                      {/* Tab Panels */}
                      <div className="rounded-2xl border border-gray-100 bg-white overflow-hidden">
                       
                        {activeTab === "state" && (
                          <>
                            <div className="bg-gray-100 px-4 py-2">
                              <h2 className="text-lg font-semibold text-blue-600 flex items-center gap-2">
                                <FaMapMarkedAlt className="text-blue-500" /> State Prices
                              </h2>
                            </div>
                            <div className="px-4 py-2">
                              {stateData.length === 0 ? (
                                <p className="text-gray-400 italic">No state price data available.</p>
                              ) : (
                                <div className="overflow-x-auto">
                                  <table className="w-full text-left">
                                    <thead>
                                      <tr className="text-gray-500 text-sm uppercase">
                                        <th className="py-3 px-2">#</th>
                                        <th className="py-3 px-2">State Name</th>
                                        <th className="py-3 px-2 text-right">Price</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                      {stateData.map((item, index) => (
                                        <tr
                                          key={`state-${index}`}
                                          className="hover:bg-blue-50 transition-colors"
                                        >
                                          <td className="py-3 px-2 font-medium text-gray-700">{index + 1}</td>
                                          <td className="py-3 px-2 font-medium text-gray-700">{item.state_name}</td>
                                          <td className="py-3 px-2 text-green-600 font-semibold text-right flex justify-end items-center gap-1">
                                            <FaRupeeSign />
                                            <span>{item.price}</span>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          </>
                        )}

                        {activeTab === "distributor" && (
                          <>
                            <div className="bg-gray-100 px-4 py-2">
                              <h2 className="text-lg font-semibold text-purple-600 flex items-center gap-2">
                                <FaUserTie className="text-purple-500" /> Distributor Prices
                              </h2>
                            </div>
                            <div className="px-4 py-2">
                              {distributorData.length === 0 ? (
                                <p className="text-gray-400 italic">No distributor price data available.</p>
                              ) : (
                                <div className="overflow-x-auto">
                                  <table className="w-full text-left">
                                    <thead>
                                      <tr className="text-gray-500 text-sm uppercase">
                                        <th className="py-3 px-2">#</th>
                                        <th className="py-3 px-2">Distributor Name</th>
                                        <th className="py-3 px-2">Distributor Code</th>
                                        <th className="py-3 px-2 text-right">Price</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                      {distributorData.map((item, index) => (
                                        <tr
                                          key={`distributor-${index}`}
                                          className="hover:bg-purple-50 transition-colors"
                                        >
                                          <td className="py-3 px-2 font-medium text-gray-700">{index + 1}</td>
                                          <td className="py-3 px-2 font-medium text-gray-700">{item.full_name}</td>
                                          <td className="py-3 px-2 font-medium text-gray-700">{item.distributor_code}</td>
                                          <td className="py-3 px-2 text-green-600 font-semibold text-right flex justify-end items-center gap-1">
                                            <FaRupeeSign />
                                            <span>{item.price}</span>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductPage;